import { Box, PopperProps, Skeleton, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { ReactNode, useRef, useState } from 'react';
import { DeviceType, useBreakpoint } from '../../hooks/useBreakpoint';
import { CfUncontrolledMenu } from '../Menu/CfUncontrolledMenu';
import { CfMenuItem, CfMenuItemCommonProps } from '../Menu/MenuItem/CfMenuItem';
import { CfTextField, CfTextFieldProps } from '../TextField/CfTextField';
import { CfContainedButton } from '../buttons/CfContainedButton';

/**
 * the properties which relate to each item of a CfSelect component
 */
export interface SelectItem<TItem> extends CfMenuItemCommonProps {
  value: string;
  label: ReactNode;
  item: TItem;
}

/**
 * the properties which describe how the CfSelect component should present and behave
 */
export interface CfSelectProps<TItem> {
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  items: SelectItem<TItem>[];
  menuSx?: SxProps<Theme>;
  inputProps?: CfTextFieldProps;
  menuItemSx?: SxProps<Theme>;
  popperProps?: PopperProps;
  popperSx?: SxProps<Theme>;
  loadingSkeletonSx?: SxProps<Theme>;
  addNewOption?: AddNewOption;

  dropdownTestId?: string;
  menuTestId?: string;
  selectButtonTestId?: string;

  onSelect?: (e: SelectItem<TItem>) => unknown;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

type AddNewOption = {
  placeholder?: string;
  onSubmit: (value: string, onComplete: () => void) => Promise<void>;
  fullWidth?: boolean;
};

/**
 *
 * @param disabled determines which icon to render
 * @param fullWidth passed to the CfTextField component's fullWidth property
 * @param items mapped to CfMenuItem components
 * @param menuSx passed to the CfUncontrolledMenu component's sx property
 * @param inputProps passed to the CfTextField component
 * @param menuItemSx augmented and passed to the CfMenuItem components' sx property
 * @param popperProps passed to the CfUncontrolledMenu component's popperProps property
 * @param popperSx augmented and passed to the CfUncontrolledMenu component's popperSx property
 * @param dropdownTestId passed to the CfTextField component's dropdownTestId property
 * @param menuTestId passed to the CfUncontrolledMenu component's dataTestId property
 * @param selectButtonTestId passed to the CfTextField component's selectButtonTestId property
 * @param onSelect executed when the CfMenuItem component's onClick or onKeyDown (enter key) event is triggered
 * @param onMenuOpen executed when the CfTextField's onClick event is triggered
 * @param onMenuClose executed when the CfUncontrolledMenu component's onClose event OR the CfTextField component's or CfMenuItem's onKeyDown (tab key) event are triggered
 */
export const CfSelect = <TItem,>(props: CfSelectProps<TItem>): JSX.Element => {
  const {
    isLoading,
    loadingSkeletonSx,
    disabled,
    fullWidth,
    items,
    inputProps,
    menuSx,
    menuItemSx,
    popperProps,
    popperSx,
    dropdownTestId,
    menuTestId,
    selectButtonTestId,
    onMenuOpen,
    onMenuClose,
    onSelect,
  } = {
    ...props,
  };

  const [isOpen, setIsOpen] = useState(false);
  const [newOption, setNewOption] = useState('');
  const tabIndex = 0;
  const anchorRef = useRef<HTMLInputElement | null>(null);
  const muiInputRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLDivElement | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const addNewRef = useRef<HTMLInputElement | null>(null);

  const firstInteractiveItemIndex: number = items.findIndex((i) => !i.isDisabled && !i.isHeader && !i.isSelected);
  const lastInteractiveItemIndex: number =
    items.length - 1 - [...items].reverse().findIndex((i) => !i.isDisabled && !i.isHeader && !i.isSelected);

  const handleMenuOpen = () => {
    setIsOpen(items.length > 0);
    onMenuOpen?.();
  };

  const handleMenuClose = () => {
    setIsOpen(false);
    muiInputRef.current?.focus();
    onMenuClose?.();
  };

  /**
   * @summary On 'enter' or 'space' key press: toggle the menu.
   * On 'tab': if the menu is open, set the focus to be the first applicable menu item instead of the next element on the page (i.e. prevent default browser behavior).
   * On 'shift' + 'tab': close the menu and allow the browser to set focus on the previous element on the page.
   */
  const handleAnchorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.code.toLowerCase()) {
      case 'enter':
      case 'space':
        handleAnchorClick();
        break;
      case 'tab':
        e.stopPropagation();

        if (e.shiftKey) {
          handleMenuClose();
        } else if (isOpen) {
          e.preventDefault();
          firstItemRef.current?.focus();
        }
        break;
    }
  };

  const handleAnchorClick = () => {
    isOpen ? handleMenuClose() : handleMenuOpen();
  };

  const handleSelectItemSelect = (value: unknown) => {
    const item = items.find((item) => item.value === value);
    if (item == undefined) return;
    handleMenuClose();
    onSelect?.(item);
  };

  const augItems = items.map((item, index) => {
    const augmentedMenuItemSx = { ...menuItemSx, ...item.sx };
    return (
      <CfMenuItem
        {...item}
        {...(firstInteractiveItemIndex === index && { rootRef: firstItemRef })}
        {...(lastInteractiveItemIndex === index && { rootRef: lastItemRef })}
        key={`cf-select-item-${index}`}
        data-testid={`cf-select-item-${index}`}
        tabIndex={tabIndex}
        onClick={() => handleSelectItemSelect(item.value)}
        onKeyDown={(e) => {
          switch (e.code.toLowerCase()) {
            case 'enter':
              handleSelectItemSelect(item.value);
              break;
            case 'tab':
              if (e.shiftKey && firstInteractiveItemIndex === index) {
                e.preventDefault();
                e.stopPropagation();
                handleMenuClose();
              } else if (!e.shiftKey && lastInteractiveItemIndex === index) {
                e.preventDefault();
                e.stopPropagation();
                if (props.addNewOption) {
                  addNewRef.current?.focus();
                } else {
                  handleMenuClose();
                }
              }
              break;
          }
        }}
        sx={{
          py: { xs: 1, xl: 0.875 },
          typography: 'responsiveParagraph',
          ...augmentedMenuItemSx,
        }}
      >
        {item.label}
      </CfMenuItem>
    );
  });

  if (props.addNewOption) {
    augItems.push(
      <CfMenuItem
        tabIndex={1}
        key='cf-select-item-add-new'
        containerSx={{ width: '100%', gap: 1, flexWrap: props.addNewOption.fullWidth ? 'wrap' : 'nowrap' }}
        sx={{
          cursor: 'default',
          py: { xs: 1, xl: 0.875 },
          borderTop: (theme: Theme) => `1px solid ${theme.palette.coolGrey[300]}`,
          ...menuItemSx,
        }}
      >
        <CfTextField
          inputRef={addNewRef}
          placeholder={props.addNewOption.placeholder}
          value={newOption}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setNewOption(event.target.value)}
          sx={props.fullWidth ? { width: '100%' } : undefined}
        />
        <CfContainedButton
          onClick={() => {
            props.addNewOption?.onSubmit(newOption, () => {
              setNewOption('');
              handleMenuClose();
            });
          }}
          isFullWidth={props.addNewOption.fullWidth}
          customOnKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (event.key === 'Tab' && !event.shiftKey) {
              handleMenuClose();
            }
          }}
        >
          Create
        </CfContainedButton>
      </CfMenuItem>
    );
  }

  const anchorComponent = (
    <CfTextField
      {...inputProps}
      inputRef={anchorRef}
      muiInputRef={muiInputRef}
      formControlTabIndex={-1}
      showIcon={true}
      readOnly={true}
      selectMode={true}
      iconClassName={disabled ? '' : isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}
      onClick={handleAnchorClick}
      onFormControlKeyDown={handleAnchorKeyDown}
      selectButtonTestId={selectButtonTestId}
      dropdownTestId={dropdownTestId}
      fullWidth={fullWidth}
      disabled={disabled}
    />
  );

  const { deviceType } = useBreakpoint();
  const augPopperSx = {
    border: 'none',
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
    borderRadius: '0px 0px 8px 8px',
    ...popperSx,
  };

  return isLoading ? (
    <Box>
      <Skeleton
        variant='rectangular'
        sx={{
          height: { xs: 40, xl: 32 },
          ...loadingSkeletonSx,
        }}
      />
    </Box>
  ) : (
    <CfUncontrolledMenu
      anchorElement={anchorRef.current}
      isOpen={isOpen}
      dataTestId={menuTestId}
      divider={undefined}
      disablePortal={false}
      firstInteractiveItemIndex={firstInteractiveItemIndex}
      lastInteractiveItemIndex={lastInteractiveItemIndex}
      inputElement={anchorComponent}
      keepMounted
      mode='select'
      variant='square'
      containerSx={{ maxHeight: 'calc(40vh)', overflowY: 'auto' }}
      placement='bottom-end'
      popperSx={augPopperSx}
      popperProps={popperProps}
      popperOffset={[31, deviceType === DeviceType.Desktop ? 6 : 8]}
      width={anchorRef.current?.parentElement?.offsetWidth}
      onClose={handleMenuClose}
      sx={menuSx}
      closeOnBlur
      stopPropagation
      disabled={props.disabled}
      keepOpenOnChange={!!props.addNewOption}
    >
      {augItems}
    </CfUncontrolledMenu>
  );
};
